// The typed bridge between the MOODS desktop shell (apps/desktop, Electron) and the web app it hosts (spec §12).
// The preload exposes it as `window.moodsDesktop` through contextBridge; the web app only talks to it through
// apps/web/src/lib/desktop.ts (which no-ops in a browser). Types only (plus a few constants) — safe to import from
// the renderer, the preload (sandboxed) and the main process.
import type { SettingsDTO } from './schemas/settings.ts';

/** `moods://…` deep links (invite, channel, profile). */
export const DESKTOP_PROTOCOL = 'moods';
/** Origin of the bundled web app inside the desktop shell (custom privileged `app://` scheme). */
export const DESKTOP_APP_ORIGIN = 'app://moods';

export type DesktopPlatform = 'win32' | 'darwin' | 'linux';

/**
 * Settings › Masaüstü › Pencere boyutu (desktop 1.0.13+). The shell keeps it on this computer (window-state.json), not
 * in settings.desktop: a size suits one screen. `auto` reopens at the size you left the window (first launch
 * 1600×1000, kept inside the screen); a fixed size always opens at that size, centred, shrunk to fit a smaller screen.
 * 16:10 sizes chosen for MOODS (owner, 2026-10-25): the sidebars and the chat use the extra height; 1100×700 fits a
 * 1366×768 laptop, 1920×1200 suits a 1440p monitor, and every one is above the 940×600 minimum.
 */
export const DESKTOP_WINDOW_SIZES = ['auto', '1100x700', '1280x800', '1440x900', '1600x1000', '1920x1200'] as const;
export type DesktopWindowSize = (typeof DESKTOP_WINDOW_SIZES)[number];

/** Runtime endpoints the shell hands to the web app (MOODS_API_URL env / userData config.json / build defaults). */
export interface DesktopConfig {
  apiUrl: string;
  gatewayUrl: string;
  siteUrl: string;
}

/** Settings › Windows/macOS Ayarları + Güncellemeler (stored in the account's settings.desktop, mirrored on disk). */
export type DesktopPreferences = SettingsDTO['desktop'];

// ───────────────────────────── updates ─────────────────────────────

export type UpdateChannel = 'stable' | 'beta';

export type UpdateState =
  /**
   * no feed configured, a development run, an unsupported platform/package format, or a Microsoft Store install
   * (`store`, shell 1.0.12+: the Store updates it)
   */
  | { phase: 'disabled'; reason: 'dev' | 'no-feed' | 'unsupported' | 'store' }
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'not-available'; checkedAt: number }
  | { phase: 'available'; version: string }
  | {
      phase: 'downloading';
      version: string;
      percent: number;
      transferred: number;
      total: number;
      bytesPerSecond: number;
    }
  /** downloaded; installs on quit or through "Yeniden başlat" */
  | { phase: 'downloaded'; version: string }
  | { phase: 'error'; message: string };

export interface UpdateCheckResult {
  available: boolean;
  version?: string;
  state: UpdateState;
}

// ───────────────────────────── window ─────────────────────────────

export interface DesktopWindowState {
  maximized: boolean;
  focused: boolean;
  fullscreen: boolean;
  canGoBack: boolean;
  canGoForward: boolean;
}

// ───────────────────────────── tray ─────────────────────────────

export type DesktopStatus = 'online' | 'idle' | 'dnd' | 'invisible';

/** What the tray menu reflects (pushed by the web app whenever it changes). */
export interface TrayState {
  signedIn: boolean;
  status: DesktopStatus;
  micOff: boolean;
  deafened: boolean;
  /** epoch ms; -1 = until unmuted ("Ben açana kadar"); null = not muted */
  notificationsMutedUntil: number | null;
  locale: 'tr' | 'en';
  unread: number;
}

export type TrayAction =
  | { type: 'open' }
  | { type: 'status'; status: DesktopStatus }
  | { type: 'toggleMute' }
  | { type: 'toggleDeafen' }
  /** minutes; null = until unmuted; 0 = unmute */
  | { type: 'muteNotifications'; minutes: number | null }
  | { type: 'checkUpdates' };

// ───────────────────────────── notifications ─────────────────────────────

export interface DesktopNotification {
  /** renderer-side id: the click event names it */
  id: string;
  title: string;
  body?: string;
  /** http(s) avatar / community icon (fetched by the main process; falls back to the app icon) */
  icon?: string | null;
  /** a newer notification with the same tag replaces the older one */
  tag?: string;
  silent?: boolean;
  /** channel id: offers the inline "Yanıtla" field (macOS); the reply comes back through `onReply` */
  replyTo?: string;
}

// ───────────────────────────── shortcuts ─────────────────────────────

/** Keybinds in the Settings › Kısayollar format (`['Ctrl', 'Shift', 'M']`, `['F8']`, `['Mouse4']`). */
export interface DesktopShortcutBindings {
  pushToTalk: string[] | null;
  toggleMute: string[] | null;
  toggleDeafen: string[] | null;
  /** show / hide the Ses Paneli — registered only while in a voice room (desktop 1.0.4+) */
  toggleOverlay?: string[] | null;
}

export type DesktopShortcutAction = keyof DesktopShortcutBindings;

/**
 * ok · unset · invalid (not expressible as a global shortcut) · conflict (another application owns it) ·
 * duplicate (two MOODS actions share the combination).
 */
export type ShortcutStatus = 'ok' | 'unset' | 'invalid' | 'conflict' | 'duplicate';

export interface DesktopShortcutReport {
  pushToTalk: { status: ShortcutStatus; mode: 'hold' | 'toggle' | 'none' };
  toggleMute: { status: ShortcutStatus };
  toggleDeafen: { status: ShortcutStatus };
  toggleOverlay?: { status: ShortcutStatus };
  /** the low-level keyboard hook (uiohook-napi) loaded: true key-down / key-up push-to-talk */
  hookAvailable: boolean;
}

export type DesktopShortcutEvent =
  { type: 'pushToTalk'; active: boolean } | { type: 'toggleMute' } | { type: 'toggleDeafen' };

// ───────────────────────────── screen share ─────────────────────────────

export interface ScreenSource {
  /** desktopCapturer source id (`screen:…` / `window:…`) */
  id: string;
  name: string;
  kind: 'screen' | 'window';
  /** PNG data URL (refreshed while the picker is open) */
  thumbnail: string;
  appIcon: string | null;
}

export interface ScreenShareSelection {
  sourceId: string;
  /** "Sesi paylaş" — system audio loopback (Windows) */
  audio: boolean;
}

// ───────────────────────────── activity ─────────────────────────────

export interface GameActivity {
  name: string;
  /** matched process name */
  process: string;
  startedAt: number;
}

// ───────────────────────────── voice overlay (Ses Paneli) ─────────────────────────────

/** One person in the voice room as the overlay shows it (resolved by the web app: nickname, community avatar). */
export interface VoiceOverlayPerson {
  id: string;
  name: string;
  /** http(s) avatar URL, null = initials */
  avatar: string | null;
  initials: string;
  /** initials tint: the UI kit's hue rotation of the brand gradient (`tintFor`, e.g. `-35deg`) */
  color: string | null;
  speaking: boolean;
  muted: boolean;
  deafened: boolean;
  self: boolean;
  /** sharing the screen */
  streaming: boolean;
  camera: boolean;
}

/** Overlay texts, already localized by the web app (the overlay page has no i18n of its own). */
export interface VoiceOverlayLabels {
  mute: string;
  unmute: string;
  deafen: string;
  undeafen: string;
  leave: string;
  open: string;
  /** "+{n} kişi" — `{n}` is replaced */
  more: string;
  /** shown in "only speakers" mode while nobody talks */
  quiet: string;
  muted: string;
  deafened: string;
  streaming: string;
  /** the header's × — hides the panel for this call (the shortcut or the tray brings it back; desktop 1.0.4+) */
  hide?: string;
}

/** The voice connection the overlay shows; the web app sends `null` when it is not in a voice room. */
export interface VoiceOverlayState {
  /** channel name (or the call's name) */
  channel: string;
  /** community name; null for DM calls */
  place: string | null;
  /** in join order */
  people: VoiceOverlayPerson[];
  /** this client's microphone / deafen state */
  muted: boolean;
  deafened: boolean;
  labels: VoiceOverlayLabels;
}

export type VoiceOverlayAction = { type: 'toggleMute' } | { type: 'toggleDeafen' } | { type: 'leave' } | { type: 'open' };

/** What the overlay page renders: the state plus the preferences it applies itself. */
export interface VoiceOverlayView {
  state: VoiceOverlayState;
  speakingOnly: boolean;
}

// ───────────────────────────── the bridge ─────────────────────────────

export interface DesktopSettingsSnapshot {
  prefs: DesktopPreferences;
  /** hardware acceleration the running process started with (a change needs a restart) */
  hardwareAccelerationActive: boolean;
  /** start-on-login state as the OS reports it */
  openAtLogin: boolean;
  /**
   * installed from the Microsoft Store (MSIX, shell 1.0.12+): Windows owns start-on-login (Ayarlar › Uygulamalar ›
   * Başlangıç) and the Store installs updates
   */
  store?: boolean;
}

/** System pages the shell opens for the web app (Windows only). */
export type DesktopSystemPage = 'startup-apps' | 'store-updates';

type Unsubscribe = () => void;

export interface MoodsDesktopBridge {
  readonly version: string;
  readonly platform: DesktopPlatform;
  readonly versions: { electron: string; chrome: string; node: string };
  readonly config: DesktopConfig;

  window: {
    minimize(): void;
    /** maximize ↔ restore */
    maximize(): void;
    close(): void;
    goBack(): void;
    goForward(): void;
    getState(): Promise<DesktopWindowState>;
    onState(cb: (s: DesktopWindowState) => void): Unsubscribe;
    /** desktop 1.0.13+: this computer's window size preference (Settings › Masaüstü) */
    getSize?(): Promise<DesktopWindowSize>;
    /** desktop 1.0.13+: keeps it and applies it now (a fixed size: un-maximizes, resizes, centres on this screen) */
    setSize?(size: DesktopWindowSize): Promise<DesktopWindowSize>;
  };

  updates: {
    getState(): Promise<UpdateState>;
    onState(cb: (s: UpdateState) => void): Unsubscribe;
    check(): Promise<UpdateCheckResult>;
    /** quit and install the downloaded update ("Yeniden başlat") */
    install(): void;
  };
  /** Settings › Güncellemeler "Şimdi denetle" */
  checkForUpdates(): Promise<UpdateCheckResult>;

  notifications: {
    show(n: DesktopNotification): void;
    onClick(cb: (id: string) => void): Unsubscribe;
    /** inline reply typed into a notification (macOS) */
    onReply(cb: (r: { id: string; text: string }) => void): Unsubscribe;
    /** count + a rendered overlay icon (PNG data URL) for the Windows taskbar; macOS shows the count on the dock */
    setBadge(count: number, overlay: string | null): void;
    /** taskbar flash (Windows) / dock bounce (macOS) while the window is not focused */
    flash(): void;
  };

  tray: {
    setState(s: TrayState): void;
    onAction(cb: (a: TrayAction) => void): Unsubscribe;
  };

  deepLinks: {
    /** in-app path (`/invite/CODE`, `/c/:community/:channel`, `/u/:username`) */
    onOpen(cb: (path: string) => void): Unsubscribe;
  };

  shortcuts: {
    register(b: DesktopShortcutBindings): Promise<DesktopShortcutReport>;
    onEvent(cb: (e: DesktopShortcutEvent) => void): Unsubscribe;
  };

  screen: {
    getSources(): Promise<ScreenSource[]>;
    /** the source the next getDisplayMedia request receives */
    select(sel: ScreenShareSelection): Promise<void>;
    cancel(): void;
    /** getDisplayMedia was called without a preselected source: show the picker */
    onPickRequest(cb: () => void): Unsubscribe;
  };

  activity: {
    current(): Promise<GameActivity | null>;
    onGame(cb: (g: GameActivity | null) => void): Unsubscribe;
    /** system idle (powerMonitor, ≥ 10 min without input or the screen locked) */
    onIdle(cb: (idle: boolean) => void): Unsubscribe;
  };

  settings: {
    get(): Promise<DesktopSettingsSnapshot>;
    relaunch(): void;
    /** Windows' startup apps page / the Store's updates (shell 1.0.12+; older shells do not have it) */
    openSystem?(page: DesktopSystemPage): void;
  };

  /** Ses Paneli (shell 1.0.3+; older shells do not have it) */
  overlay?: {
    /** the voice room to show, null when not connected (the shell decides when the window is visible) */
    setState(s: VoiceOverlayState | null): void;
    /** a button pressed in the overlay (mute, deafen, leave; "open" is handled by the shell) */
    onAction(cb: (a: VoiceOverlayAction) => void): Unsubscribe;
    /** back to the default top-right corner */
    resetPosition(): void;
  };
  /** push Settings › Masaüstü preferences to the shell (login item, tray, updater channel, game detection…) */
  applySettings(prefs: DesktopPreferences): void;

  auth: {
    /** refresh token kept in OS-encrypted storage (safeStorage) */
    getRefreshToken(): Promise<string | null>;
    setRefreshToken(token: string | null): Promise<void>;
    /**
     * "Known device" token (new-device verification) in OS-encrypted storage; unlike the refresh token it survives
     * logging out, like the browsers' `moods_dt` cookie. Optional: shells before 1.0.1 do not have it.
     */
    getDeviceToken?(): Promise<string | null>;
    setDeviceToken?(token: string | null): Promise<void>;
  };
}
